import {
    AdMob,
    BannerAdPluginEvents,
    BannerAdPosition,
    BannerAdSize,
    RewardInterstitialAdPluginEvents,
} from '@capacitor-community/admob';
import {
    bannerBottomMargin,
    createAdConsent,
    watchBannerMargin,
} from '@gagovictor/shared-frontend-core/dist/ads';

import { config, isNative } from './config';

/** Google's consent form (UMP) for the EEA, UK and Switzerland. Ads start only after it allows them. */
export const adConsent = createAdConsent({ plugin: AdMob, isNative });

let initialized = false;

/** Starts the ad SDK. A no-op in the browser. A failure only means no ads are shown. */
export const initializeAdMob = async (): Promise<void> => {
    if (!isNative() || initialized) return;
    if (!(await adConsent.gather())) return;
    try {
        await AdMob.initialize({ initializeForTesting: config.admobTesting });
        initialized = true;
    } catch (error) {
        console.warn('Ads could not start:', (error as Error)?.message);
    }
};

/**
 * - rewarded: the ad was watched to the end;
 * - dismissed: it was closed early, so the reading stays locked;
 * - unavailable: no ad could be loaded or shown (offline, no fill). The caller lets the person through,
 *   so a missing ad never traps them.
 */
export type RewardedResult = 'rewarded' | 'dismissed' | 'unavailable';

/** Loads and shows one rewarded interstitial (arcana-beforereveal). Always resolves. */
export const showRewarded = async (): Promise<RewardedResult> => {
    if (!isNative()) return 'unavailable';
    await initializeAdMob();
    if (!initialized) return 'unavailable';

    const handles: { remove: () => Promise<void> | void }[] = [];
    try {
        return await new Promise<RewardedResult>((resolve) => {
            let rewarded = false;
            Promise.all([
                AdMob.addListener(
                    RewardInterstitialAdPluginEvents.Rewarded,
                    () => {
                        rewarded = true;
                    }
                ),
                AdMob.addListener(
                    RewardInterstitialAdPluginEvents.Dismissed,
                    () => resolve(rewarded ? 'rewarded' : 'dismissed')
                ),
                AdMob.addListener(
                    RewardInterstitialAdPluginEvents.FailedToLoad,
                    () => resolve('unavailable')
                ),
                AdMob.addListener(
                    RewardInterstitialAdPluginEvents.FailedToShow,
                    () => resolve('unavailable')
                ),
            ])
                .then(async (listeners) => {
                    handles.push(...listeners);
                    await AdMob.prepareRewardInterstitialAd({
                        adId: config.admobRewardedId,
                        isTesting: config.admobTesting,
                    });
                    await AdMob.showRewardInterstitialAd();
                })
                .catch(() => resolve('unavailable'));
        });
    } finally {
        await Promise.all(
            handles.map((h) =>
                Promise.resolve(h.remove()).catch(() => undefined)
            )
        );
    }
};

/**
 * Shows the adaptive banner at the bottom of the phone app and reports its height (px), so the page can
 * leave room for it. Returns a function that removes the banner.
 */
export const showBanner = async (
    onHeight: (px: number) => void
): Promise<() => void> => {
    if (!isNative()) return () => undefined;
    await initializeAdMob();
    if (!initialized) return () => undefined;
    const handle = await AdMob.addListener(
        BannerAdPluginEvents.SizeChanged,
        (size) => onHeight(size.height)
    );
    const show = async () => {
        try {
            await AdMob.showBanner({
                adId: config.admobBannerId,
                adSize: BannerAdSize.ADAPTIVE_BANNER,
                position: BannerAdPosition.BOTTOM_CENTER,
                margin: bannerBottomMargin(),
                isTesting: config.admobTesting,
            });
        } catch (error) {
            console.warn('Banner failed:', (error as Error)?.message);
        }
    };
    await show();
    const stopWatching = watchBannerMargin(() => void show());
    return () => {
        stopWatching();
        void handle.remove();
        void AdMob.removeBanner().catch(() => undefined);
        onHeight(0);
    };
};
